import { inject, Injectable } from "@angular/core";
import { DateTime } from "luxon";
import { combineLatest, Observable, of } from "rxjs";
import { catchError, concatMap, map, tap } from "rxjs/operators";
import { UtilsService } from "src/app/shared/services/utils.service";
import { Shower, ShowerAdapter } from "../models/shower.model";
import {
  HydraoApiService,
  ResponseWithHeaders,
} from "./core/hydrao-api.service";

class ShowerPage {
  constructor(public data: Shower[], public totalCount: number) {}
}

@Injectable({
  providedIn: "root",
})
export class ShowerService extends HydraoApiService<Shower> {
  private showerAdapter: ShowerAdapter = inject(ShowerAdapter);
  private _utils: UtilsService = inject(UtilsService);

  private cachedShower: Set<string> = new Set();

  public emptyCache(): Observable<any> {
    let clearObservables: Observable<any>[] = [];
    for (let cachedUrl of this.cachedShower) {
      clearObservables.push(
        this.cacheService.clear(cachedUrl).pipe(
          catchError(() => {
            this.logger.warn("error clearing " + cachedUrl);
            return null;
          })
        )
      );
    }
    if (clearObservables.length == 0) {
      return of(null);
    } else {
      return combineLatest(clearObservables).pipe(
        tap(() => {
          this.cachedShower = new Set();
        })
      );
    }
  }

  public getShowerPortion(
    deviceUuid: string,
    fromId: number,
    toId: number
  ): Observable<Shower[]> {
    let url = `/shower-heads/${deviceUuid}/showers?fromid=${fromId}&toid=${toId}`;
    this.cachedShower.add(url);

    return this.get(url, this.showerAdapter).pipe(
      tap((showers: Shower[]) => {
        showers.sort((s1, s2) => s1.shower_id - s2.shower_id);
      })
    ) as Observable<Shower[]>;
  }

  public getShowerheadRawStatsForStore(
    uuid: string,
    fromDate: DateTime,
    toDate: DateTime
  ): Observable<Shower[]> {
    let url = "/user-stats/v2/showerheads/" + uuid;

    url += "?timezone=" + encodeURIComponent(this._utils.getTimezone());
    if (fromDate) {
      url += "&fromdate=" + encodeURIComponent(fromDate.toISO());
    }
    if (toDate) {
      url += "&todate=" + encodeURIComponent(toDate.toISO());
    }

    return this.get(url, this.showerAdapter, true, true).pipe(
      tap((showers: Shower[]) => {
        showers.sort((s1, s2) => s1.shower_id - s2.shower_id);
      })
    ) as Observable<Shower[]>;
  }

  public getShowers(
    deviceUuid: string,
    limit: number,
    offset: number,
    refresh: boolean = false
  ): Observable<ShowerPage> {
    let url =
      "/shower-heads/" + deviceUuid + "/showers?limit=" + (limit ? limit : 50);
    if (offset) {
      url += "&offset=" + offset;
    }
    this.cachedShower.add(url);

    if (refresh) {
      this.cacheService.clear(url);
    }

    let response = this.getWithHeaders(url, this.showerAdapter) as Observable<
      ResponseWithHeaders<Shower[]>
    >;
    return response.pipe(
      map((resp: ResponseWithHeaders<Shower[]>) => {
        let contentRange = resp.headers.get("content-range");
        let count = resp.data.length;
        if (contentRange) {
          let match = contentRange.match(/\d*-\d*\/(\d*)/);

          if (match.length > 0 && !isNaN(Number(match[1]))) {
            count = parseInt(match[1]);
          }
        }

        return new ShowerPage(resp.data, count);
      })
    );
  }

  public getLiveShower(deviceUuid: string): Observable<Shower> {
    let url = "/shower-heads/" + deviceUuid + "/live-shower";
    //refresh each 20 seconds
    return this.get(url, this.showerAdapter, false, false, 20).pipe(
      concatMap(async (shower: Shower) => {
        shower.is_live = true;
        shower.is_real_date = true;
        return shower;
      })
    ) as Observable<Shower>;
  }
}
